import React from "react";
import AnimateHeight from "react-animate-height";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useCockpitPreferences } from "@/hooks/play/useCockpitPreferences";
import "./CollapsibleDataCard.scss";

interface CollapsibleDataCardProps {
    id: string;
    title: string;
    icon?: React.ReactNode;
    defaultExpanded?: boolean;
    children: React.ReactNode;
    className?: string;
    badge?: string | number;
    headerActions?: React.ReactNode;
    isLoading?: boolean;
    emptyState?: React.ReactNode;
    hasData?: boolean;
}

/**
 * Collapsible card component for CockpitV3 dashboard
 * Remembers expanded/collapsed state in localStorage
 */
const CollapsibleDataCard: React.FC<CollapsibleDataCardProps> = ({
    id,
    title,
    icon,
    defaultExpanded = true,
    children,
    className = "",
    badge,
    headerActions,
    isLoading = false,
    emptyState,
    hasData = true,
}) => {
    const { isExpanded, toggleCard } = useCockpitPreferences();
    const expanded = isExpanded(id, defaultExpanded);

    const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (e.type === "click" || (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === " ") {
            toggleCard(id);
        }
    };

    return (
        <div className={`collapsible-data-card ${className} ${expanded ? "expanded" : "collapsed"}`}>
            <div
                className="collapsible-data-card__header"
                onClick={handleToggle}
                onKeyDown={handleToggle}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls={`${id}-content`}
            >
                <div className="collapsible-data-card__title-group">
                    {icon && <span className="collapsible-data-card__icon">{icon}</span>}
                    <h3 className="collapsible-data-card__title">{title}</h3>
                    {badge !== undefined && badge !== null &&
            <span className="collapsible-data-card__badge">{badge}</span>
                    }
                </div>

                <div className="collapsible-data-card__actions">
                    {headerActions && expanded &&
            <div className="collapsible-data-card__header-actions" onClick={(e) => e.stopPropagation()}>
                {headerActions}
            </div>
                    }
                    <button
                        className="collapsible-data-card__toggle"
                        aria-label={expanded ? "Collapse" : "Expand"}
                    >
                        {expanded ? <FiChevronUp /> : <FiChevronDown />}
                    </button>
                </div>
            </div>

            <AnimateHeight
                id={`${id}-content`}
                duration={200}
                height={expanded ? "auto" : 0}
                animateOpacity={true}
                className="collapsible-data-card__content"
            >
                <div className="collapsible-data-card__inner">
                    {isLoading
                        ? <div className="collapsible-data-card__loading">
                            <div className="loading-spinner" />
                            <span>Loading...</span>
                        </div>
                        : !hasData && emptyState
                            ? <div className="collapsible-data-card__empty">
                                {emptyState}
                            </div>
                            : children
                    }
                </div>
            </AnimateHeight>
        </div>
    );
};

export default CollapsibleDataCard;